import type { JSX } from 'react';
import { useCallback } from 'react';
import type { Key } from 'react-aria';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useContextKinds } from '@gonfalon/contexts/hooks/useContextKinds';
import { ContextualFeedbackTrigger } from '@gonfalon/feedback/ContextualFeedbackTrigger';
import type { ContextAttributeFilter, EventPropertyFilter, GroupFilter, MetricFilter } from '@gonfalon/metrics/types';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import {
  Button,
  ComboBox,
  Group,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { EventFilterInfoTooltip } from '../components/EventFilterInfoTooltip';
import { FilterBase } from '../FilterBase';
import { FilterSelectionPopover } from '../FilterSelectionPopover';
import type { MetricForm } from '../formUtils';
import { defaultContextAttributeFilter, defaultEventPropertyFilter } from '../formUtils';

import {
  addFilter,
  handleAddSubFilter,
  handleOpChange,
  removeSubFilter,
  updateSubFilter,
  validateFilter,
} from './filterUtils';

import styles from './MetricEventFilters.module.css';

interface MetricEventFiltersProps {
  context: UseFormReturn<MetricForm>;
  headerTitle?: string;
  showInfoTooltip?: boolean;
}

export const MetricEventFilters: React.FC<MetricEventFiltersProps> = ({
  context,
  headerTitle = 'Events filtered by',
  showInfoTooltip = false,
}) => {
  const { setValue, register } = context;
  const baseField = useWatch({ control: context.control, name: 'filters' });
  if (!baseField) {
    return null;
  }

  return (
    <div className={styles.filtersContainer}>
      <div className={styles.filtersHeader}>
        <span className={styles.headerTitleGroup}>
          <span className={styles.headerText}>{headerTitle}</span>
          {showInfoTooltip && <EventFilterInfoTooltip />}
        </span>
        <ContextualFeedbackTrigger flagKey="feedback-recipient-metric-event-filters" variant="minimal" />
      </div>
      <EventFilterRecursive
        filter={baseField}
        onUpdate={(filter: MetricFilter) => setValue('filters', filter, { shouldDirty: true, shouldValidate: true })}
        onRemove={() => setValue('filters', undefined, { shouldDirty: true, shouldValidate: true })}
        depth={0}
        {...register('filters', { validate: validateFilter })}
      />
    </div>
  );
};

interface EventFilterRecursiveProps {
  filter: MetricFilter;
  onUpdate: (filter: MetricFilter) => void;
  onRemove: () => void;
  depth: number;
  showSubFilter?: boolean;
  onAddSubFilter?: (chosenFilter: MetricFilter) => void;
}

const EventFilterRecursive: React.FC<EventFilterRecursiveProps> = ({
  filter,
  onRemove,
  onUpdate,
  depth,
  showSubFilter,
  onAddSubFilter,
}) => {
  const handleNewFilter = (newFilter: MetricFilter) => {
    addFilter(onUpdate, filter, newFilter);
  };

  let filterSection: JSX.Element | null = null;

  if (filter.type === 'group') {
    filterSection = <GroupFilterSection filter={filter} depth={depth} onUpdate={onUpdate} />;
  } else if (filter.type === 'contextAttribute') {
    filterSection = (
      <ContextAttributeFilterSection
        filter={filter}
        onUpdate={onUpdate}
        onRemove={onRemove}
        showSubFilter={showSubFilter}
        onAddSubFilter={onAddSubFilter}
      />
    );
  } else if (filter.type === 'eventProperty') {
    filterSection = (
      <EventPropertyFilterSection
        filter={filter}
        onUpdate={onUpdate}
        onRemove={onRemove}
        showSubFilter={showSubFilter}
        onAddSubFilter={onAddSubFilter}
      />
    );
  }

  return (
    <div>
      {filterSection}
      {depth === 0 && (
        <FilterSelectionPopover
          onSelectContextKind={() => handleNewFilter(defaultContextAttributeFilter)}
          onSelectEventProperty={() => handleNewFilter(defaultEventPropertyFilter)}
        >
          <Button aria-label="add" variant="minimal" className={styles.addFilterButton}>
            <span className={styles.iconSpan}>
              <Icon name="add" size="tiny" aria-hidden="true" /> Add filter
            </span>
          </Button>
        </FilterSelectionPopover>
      )}
    </div>
  );
};

interface ContextAttributeFilterProps {
  filter: ContextAttributeFilter;
  onUpdate: (filter: MetricFilter) => void;
  onRemove: () => void;
  showSubFilter?: boolean;
  onAddSubFilter?: (chosenFilter: MetricFilter) => void;
}

const ContextAttributeFilterSection: React.FC<ContextAttributeFilterProps> = (props) => {
  const { filter, onUpdate } = props;
  const projectKey = useProjectKey();
  const { contextKinds } = useContextKinds(projectKey);

  const handleContextKindChange = useCallback(
    (key: Key | null) => {
      if (key) {
        onUpdate({ ...filter, contextKind: key.toString(), attribute: '', values: [] });
      }
    },
    [filter, onUpdate],
  );

  return (
    <FilterBase
      {...props}
      isOperatorDisabled={!filter.contextKind}
      attributePlaceholder="Select a context attribute"
      isAttributeDisabled={!filter.contextKind}
    >
      <ComboBox
        name="Context kind"
        selectedKey={filter.contextKind}
        aria-label="Select a context kind"
        className={styles.selectContextKind}
        onSelectionChange={handleContextKindChange}
        isInvalid={!filter.contextKind}
      >
        <Group>
          <Input placeholder="Select a context kind" />
          <Icon name="chevron-down" size="small" aria-hidden="true" />
        </Group>
        <Popover>
          <ListBox className={styles.selectContextKindListBox}>
            {contextKinds.map((kind) => (
              <ListBoxItem id={kind.key} key={kind.key}>
                {kind.name}
              </ListBoxItem>
            ))}
          </ListBox>
        </Popover>
      </ComboBox>
    </FilterBase>
  );
};

interface EventPropertyFilterProps {
  filter: EventPropertyFilter;
  onUpdate: (filter: MetricFilter) => void;
  onRemove: () => void;
  showSubFilter?: boolean;
  onAddSubFilter?: (chosenFilter: MetricFilter) => void;
}

const EventPropertyFilterSection: React.FC<EventPropertyFilterProps> = (props) => (
  <FilterBase {...props} attributePlaceholder="Choose an event property" />
);

interface GroupFilterProps {
  filter: GroupFilter;
  depth: number;
  onUpdate: (filter: MetricFilter) => void;
}

const GroupFilterSection: React.FC<GroupFilterProps> = ({ filter, depth, onUpdate }) => (
  <div>
    {filter.values.map((f, index) => (
      <div key={index} className={styles.operatorHolder}>
        <div className={styles.filterGroup}>
          <EventFilterRecursive
            filter={f}
            depth={depth + 1}
            onUpdate={(updated) => updateSubFilter(onUpdate, filter, updated, index)}
            onRemove={() => removeSubFilter(onUpdate, filter, index)}
            showSubFilter={depth === 0 && f.type !== 'group'}
            onAddSubFilter={(chosenFilter: MetricFilter) =>
              handleAddSubFilter(onUpdate, filter, index, f, chosenFilter)
            }
          />
          {depth === 0 && f.type === 'group' && (
            <FilterSelectionPopover
              onSelectContextKind={() => handleAddSubFilter(onUpdate, filter, index, f, defaultContextAttributeFilter)}
              onSelectEventProperty={() => handleAddSubFilter(onUpdate, filter, index, f, defaultEventPropertyFilter)}
            >
              <Button aria-label="add" variant="minimal" className={styles.addSubFilterButton}>
                <span className={styles.iconSpan}>
                  <Icon name="add" size="tiny" aria-hidden="true" />
                  Add another filter
                </span>
              </Button>
            </FilterSelectionPopover>
          )}
        </div>
        {index < filter.values.length - 1 && (
          <AndOrToggle
            state={filter.op}
            onChange={(keys) => handleOpChange(onUpdate, filter, keys)}
            isDisabled={index !== 0}
            className={clsx(styles.andOrToggle, {
              [styles.andOrTogglePadding]: depth > 0,
            })}
          />
        )}
      </div>
    ))}
  </div>
);

interface AndOrToggleProps {
  state: 'and' | 'or';
  onChange: (keys: Set<Key>) => void;
  isDisabled: boolean;
  className?: string;
}

const AndOrToggle: React.FC<AndOrToggleProps> = ({ state, onChange, isDisabled, className }) => (
  <ToggleButtonGroup isDisabled={isDisabled} onSelectionChange={onChange} selectedKeys={[state]} className={className}>
    <div>
      <ToggleButton id="and" size="small">
        And
      </ToggleButton>
      <ToggleButton id="or" size="small">
        Or
      </ToggleButton>
    </div>
  </ToggleButtonGroup>
);
